import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, CardActionArea, Fab, Stack,
  Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Badge
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'
import { GroupSummary } from '@/types'
import { useGroupHub } from '@/hooks/useGroupHub'

export default function GroupList() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [groups, setGroups] = useState<GroupSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [creating, setCreating] = useState(false)

  const loadGroups = useCallback(async () => {
    try {
      const data = await apiClient.get<GroupSummary[]>('/api/groups')
      setGroups(data)
    } catch {
      enqueueSnackbar('Failed to load groups', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => { loadGroups() }, [loadGroups])

  // Real-time updates via SignalR
  const groupIds = useMemo(() => groups.map(g => g.id), [groups])
  useGroupHub(groupIds, {
    onPrayerRequestCreated: loadGroups,
    onPrayerRequestUpdated: loadGroups,
    onPrayerRequestDeleted: loadGroups,
    onDiscussionCreated: loadGroups,
    onDiscussionUpdated: loadGroups,
    onDiscussionDeleted: loadGroups,
  })

  const handleCreate = async () => {
    if (!newGroupName.trim()) return
    setCreating(true)
    try {
      const group = await apiClient.post<GroupSummary>('/api/groups', { name: newGroupName.trim() })
      setGroups(prev => [...prev, group])
      setCreateOpen(false)
      setNewGroupName('')
      enqueueSnackbar('Group created', { variant: 'success' })
      navigate(`/groups/${group.id}`)
    } catch {
      enqueueSnackbar('Failed to create group', { variant: 'error' })
    } finally {
      setCreating(false)
    }
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        My Groups
      </Typography>

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} />)}
        </Stack>
      ) : groups.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" gutterBottom>
              You haven't joined any groups yet.
            </Typography>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              Create Your First Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1}>
          {groups.map(group => (
            <Card key={group.id} variant="outlined">
              <CardActionArea onClick={() => navigate(`/groups/${group.id}`)}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{group.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {group.unviewedCount > 0 && (
                      <Badge badgeContent={group.unviewedCount} color="error" />
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}

      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 72, right: 16 }}
        onClick={() => setCreateOpen(true)}
        aria-label="Create group"
      >
        <Add />
      </Fab>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Group Name"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating || !newGroupName.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
